/**
 * DateRangeFilter - Filtro de rango de fechas con presets relativos
 *
 * Componente reutilizable para filtrar por rango de fechas.
 * Ofrece presets rápidos (Hoy, Últimos 7 días, etc.) y un modo
 * de rango personalizado con dos DatePickers.
 *
 * Diseñado para integrarse con useServerDataTable via setFilter().
 */

import * as React from "react";
import {
  format,
  startOfDay,
  endOfDay,
  subDays,
  startOfMonth,
  endOfMonth,
  subMonths,
  startOfWeek,
  parse,
  isValid,
} from "date-fns";
import { es, enUS, ptBR, type Locale } from "date-fns/locale";
import { CalendarIcon, CalendarRange, X } from "lucide-react";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

// ============================================
// TYPES
// ============================================

export interface DatePreset {
  key: string;
  labelKey: string;
  getRange: () => { desde: string; hasta: string };
}

export interface DateRangeFilterProps {
  /** Valor actual de fecha_desde (YYYY-MM-DD o "") */
  desde: string;
  /** Valor actual de fecha_hasta (YYYY-MM-DD o "") */
  hasta: string;
  /** Callback cuando cambia el rango */
  onChange: (desde: string, hasta: string) => void;
  /** Presets personalizados (usa defaults si no se provee) */
  presets?: DatePreset[];
  /** Clase CSS adicional */
  className?: string;
  /** Deshabilitar el componente */
  disabled?: boolean;
}

// ============================================
// LOCALES
// ============================================

const locales: Record<string, Locale> = { es, en: enUS, pt: ptBR };

// ============================================
// DEFAULT PRESETS
// ============================================

const fmt = (d: Date) => format(d, "yyyy-MM-dd");

export function getDefaultPresets(): DatePreset[] {
  return [
    {
      key: "today",
      labelKey: "dateFilter.presets.today",
      getRange: () => {
        const now = new Date();
        return { desde: fmt(startOfDay(now)), hasta: fmt(endOfDay(now)) };
      },
    },
    {
      key: "yesterday",
      labelKey: "dateFilter.presets.yesterday",
      getRange: () => {
        const yesterday = subDays(new Date(), 1);
        return { desde: fmt(startOfDay(yesterday)), hasta: fmt(endOfDay(yesterday)) };
      },
    },
    {
      key: "last7",
      labelKey: "dateFilter.presets.last7Days",
      getRange: () => ({
        desde: fmt(startOfDay(subDays(new Date(), 6))),
        hasta: fmt(endOfDay(new Date())),
      }),
    },
    {
      key: "last30",
      labelKey: "dateFilter.presets.last30Days",
      getRange: () => ({
        desde: fmt(startOfDay(subDays(new Date(), 29))),
        hasta: fmt(endOfDay(new Date())),
      }),
    },
    {
      key: "thisMonth",
      labelKey: "dateFilter.presets.thisMonth",
      getRange: () => ({
        desde: fmt(startOfMonth(new Date())),
        hasta: fmt(endOfDay(new Date())),
      }),
    },
    {
      key: "lastMonth",
      labelKey: "dateFilter.presets.lastMonth",
      getRange: () => {
        const prev = subMonths(new Date(), 1);
        return { desde: fmt(startOfMonth(prev)), hasta: fmt(endOfMonth(prev)) };
      },
    },
    {
      key: "thisWeek",
      labelKey: "dateFilter.presets.thisWeek",
      getRange: () => ({
        desde: fmt(startOfWeek(new Date(), { weekStartsOn: 1 })),
        hasta: fmt(endOfDay(new Date())),
      }),
    },
  ];
}

// ============================================
// HELPERS
// ============================================

function parseDate(str: string): Date | undefined {
  if (!str) return undefined;
  const d = parse(str, "yyyy-MM-dd", new Date());
  return isValid(d) ? d : undefined;
}

function formatDisplay(str: string, locale: Locale): string {
  const d = parseDate(str);
  if (!d) return "";
  return format(d, "dd MMM yyyy", { locale });
}

function detectActivePreset(desde: string, hasta: string, presets: DatePreset[]): string | null {
  if (!desde && !hasta) return null;
  for (const preset of presets) {
    const range = preset.getRange();
    if (range.desde === desde && range.hasta === hasta) {
      return preset.key;
    }
  }
  return "custom";
}

// ============================================
// COMPONENT
// ============================================

export function DateRangeFilter({
  desde,
  hasta,
  onChange,
  presets,
  className,
  disabled = false,
}: DateRangeFilterProps) {
  const { t, i18n } = useTranslation();
  const langCode = i18n.language?.split("-")[0] || "es";
  const locale = locales[langCode] || es;

  const effectivePresets = presets ?? getDefaultPresets();
  const [open, setOpen] = React.useState(false);
  const [showCustom, setShowCustom] = React.useState(false);

  const activePreset = React.useMemo(
    () => detectActivePreset(desde, hasta, effectivePresets),
    [desde, hasta, effectivePresets]
  );

  const hasValue = desde !== "" || hasta !== "";

  // Detectar si estamos en modo custom al abrir
  React.useEffect(() => {
    if (open && activePreset === "custom") {
      setShowCustom(true);
    }
  }, [open, activePreset]);

  const handlePresetClick = (preset: DatePreset) => {
    const range = preset.getRange();
    onChange(range.desde, range.hasta);
    setShowCustom(false);
    setOpen(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange("", "");
    setShowCustom(false);
  };

  const handleDesdeChange = (date: Date | undefined) => {
    if (date) {
      const newDesde = fmt(date);
      // Si hasta está vacío o es menor que desde, ajustar
      if (!hasta || newDesde > hasta) {
        onChange(newDesde, newDesde);
      } else {
        onChange(newDesde, hasta);
      }
    }
  };

  const handleHastaChange = (date: Date | undefined) => {
    if (date) {
      const newHasta = fmt(date);
      // Si desde está vacío o es mayor que hasta, ajustar
      if (!desde || newHasta < desde) {
        onChange(newHasta, newHasta);
      } else {
        onChange(desde, newHasta);
      }
    }
  };

  // Label del botón trigger
  const triggerLabel = React.useMemo(() => {
    if (!hasValue) return t("dateFilter.selectDate");
    if (activePreset && activePreset !== "custom") {
      const preset = effectivePresets.find((p) => p.key === activePreset);
      return preset ? t(preset.labelKey) : t("dateFilter.selectDate");
    }
    // Rango custom
    const desdeStr = formatDisplay(desde, locale);
    const hastaStr = formatDisplay(hasta, locale);
    if (desde === hasta) return desdeStr;
    return `${desdeStr} – ${hastaStr}`;
  }, [hasValue, activePreset, desde, hasta, effectivePresets, locale, t]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          disabled={disabled}
          className={cn(
            "justify-start text-left font-normal gap-2",
            !hasValue && "text-muted-foreground",
            className
          )}
        >
          <CalendarRange className="h-4 w-4 shrink-0" />
          <span className="truncate">{triggerLabel}</span>
          {hasValue && (
            <span
              role="button"
              tabIndex={0}
              onClick={handleClear}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ")
                  handleClear(e as unknown as React.MouseEvent);
              }}
              className="ml-auto rounded-full p-0.5 hover:bg-muted"
              aria-label={t("common.clearFilters")}
            >
              <X className="h-3 w-3" />
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="flex">
          {/* Presets sidebar */}
          <div className="border-r p-2 space-y-0.5 min-w-[160px]">
            {effectivePresets.map((preset) => (
              <button
                key={preset.key}
                onClick={() => handlePresetClick(preset)}
                className={cn(
                  "w-full text-left text-sm px-3 py-1.5 rounded-md transition-colors",
                  "hover:bg-accent hover:text-accent-foreground",
                  activePreset === preset.key &&
                    "bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground"
                )}
              >
                {t(preset.labelKey)}
              </button>
            ))}
            <div className="border-t my-1.5" />
            <button
              onClick={() => setShowCustom(true)}
              className={cn(
                "w-full text-left text-sm px-3 py-1.5 rounded-md transition-colors",
                "hover:bg-accent hover:text-accent-foreground",
                activePreset === "custom" &&
                  "bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground"
              )}
            >
              <span className="flex items-center gap-2">
                <CalendarIcon className="h-3.5 w-3.5" />
                {t("dateFilter.customRange")}
              </span>
            </button>
          </div>

          {/* Custom range calendars */}
          {showCustom && (
            <div className="p-3 space-y-3">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="space-y-1.5">
                  <p className="text-xs font-medium text-muted-foreground px-1">
                    {t("dateFilter.from")}
                  </p>
                  <Calendar
                    key={`desde-${i18n.language}`}
                    mode="single"
                    selected={parseDate(desde)}
                    onSelect={handleDesdeChange}
                    defaultMonth={parseDate(desde)}
                    toDate={new Date()}
                    locale={locale}
                    captionLayout="dropdown"
                    fromYear={2020}
                    toYear={new Date().getFullYear()}
                    initialFocus
                  />
                </div>
                <div className="space-y-1.5">
                  <p className="text-xs font-medium text-muted-foreground px-1">
                    {t("dateFilter.to")}
                  </p>
                  <Calendar
                    key={`hasta-${i18n.language}`}
                    mode="single"
                    selected={parseDate(hasta)}
                    onSelect={handleHastaChange}
                    defaultMonth={parseDate(hasta) || parseDate(desde)}
                    fromDate={parseDate(desde)}
                    toDate={new Date()}
                    locale={locale}
                    captionLayout="dropdown"
                    fromYear={2020}
                    toYear={new Date().getFullYear()}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
